import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import HeroSection from './components/HeroSection';
import TrustBar from './components/TrustBar';
import CategorySection from './components/CategorySection';
import ProductsSection from './components/ProductsSection';
import TrendingProducts from './components/TrendingProducts';
import ZenPhilosophy from './components/ZenPhilosophy';
import RouletteWheel from '../../../components/RouletteWheel';
import AuthPopup from '../../../components/AuthPopup';
import { useHomePageViewModel } from './viewmodels/home-page-viewmodel';
import { formatCategoryName } from './utils/category-format';
import './home-page.css';

const HomePage = () => {
  const [showRoulette, setShowRoulette] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  
  const {
    // Products state
    products,
    loading,
    error,
    
    // Filter states
    selectedCategory,
    setSelectedCategory,
    
    // Brand data
    brands,
    
    // Computed values
    filteredProducts,
    hasMore,
    loadingMore,
    loadMoreProducts,
    loadMoreError,
    retryLoadMore,
    
    // Actions
    resetFilters,
    applyFilters
  } = useHomePageViewModel();

  // Show roulette popup on home visit (once per session)
  useEffect(() => {
    const hasSeenRoulette = sessionStorage.getItem('rouletteShown');
    if (!hasSeenRoulette) {
      const timer = setTimeout(() => {
        setShowRoulette(true);
        sessionStorage.setItem('rouletteShown', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNeedLogin = useCallback(() => {
    setShowRoulette(false);
    setShowAuthPopup(true);
  }, []);

  const handleAuthSuccess = useCallback(() => {
    setShowAuthPopup(false);
    // Re-open roulette after successful login/signup
    setTimeout(() => setShowRoulette(true), 500);
  }, []);

  // "Acheter la collection" in the hero filters the shop by that category
  const handleHeroCategory = useCallback((brandId) => {
    setSelectedCategory(String(brandId));
    applyFilters({ marqueId: brandId });
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  }, [applyFilters, setSelectedCategory]);

  const handleShowAllProducts = useCallback(() => {
    setSelectedCategory('all');
    applyFilters({ marqueId: null });
  }, [applyFilters, setSelectedCategory]);

  const activeCategoryName = useMemo(() => {
    const active = brands.find(brand => String(brand.id) === String(selectedCategory));
    return active ? formatCategoryName(active.name) : '';
  }, [brands, selectedCategory]);

  // Photo for the brand story; the sabot shot reads best there
  const storyImage = useMemo(() => {
    const withImages = brands.filter(brand => brand.imagePath);
    return (withImages.find(brand => /sabot/i.test(brand.name)) ?? withImages[0])?.imagePath ?? null;
  }, [brands]);

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Navbar />
      
      {/* Add top padding (pt-16 or pt-20) to push content below the fixed navbar */}
      <main className="flex-grow pt-20">
        {/* Collections slideshow */}
        <HeroSection
          showcase={brands}
          pending={loading && brands.length === 0}
          onSelectCategory={handleHeroCategory}
        />

        <TrustBar />

        {/* Brand story / values */}
        <ZenPhilosophy image={storyImage} />
        
        {/* Categories section */}
        <CategorySection 
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          brands={brands}
          onSelectCategory={applyFilters}
        />
        
        {/* Main products section with filters */}
        <ProductsSection 
          products={products}
          filteredProducts={filteredProducts}
          loading={loading}
          error={error}
          resetFilters={resetFilters}
          hasMore={hasMore}
          loadingMore={loadingMore}
          loadMoreProducts={loadMoreProducts}
          loadMoreError={loadMoreError}
          retryLoadMore={retryLoadMore}
          selectedCategory={selectedCategory}
          activeCategoryName={activeCategoryName}
          onShowAll={handleShowAllProducts}
        />
        
        {/* Trending products section */}
        <TrendingProducts products={filteredProducts.slice(0, 3)} />
        
      </main>
      
      <Footer />

      {/* Floating Roulette Button */}
      <button
        onClick={() => setShowRoulette(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-br from-[#174540] to-[#2a7a6f] rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group"
        title="Tourner la roue de la chance"
      >
        <span className="text-2xl group-hover:animate-spin" style={{ animationDuration: '2s' }}>🎰</span>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
      </button>

      {/* Roulette Popup */}
      <RouletteWheel
        isOpen={showRoulette}
        onClose={() => setShowRoulette(false)}
        onNeedLogin={handleNeedLogin}
      />

      {/* Auth Popup (for login/signup before spinning) */}
      <AuthPopup
        isOpen={showAuthPopup}
        onClose={() => setShowAuthPopup(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default HomePage;